import { useMemo, useState } from "react";
import type { UsageDay } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { usageMessages } from "../pages/usage.i18n";

const W = 1100;
const H = 300;
const M = { top: 16, right: 132, bottom: 30, left: 72 };

/** Passo "redondo" (1, 2, 2,5 ou 5 × 10^n GB) para 4 intervalos. */
function niceStep(max: number): number {
  const raw = max / 4 / 1e9;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const f = [1, 2, 2.5, 5, 10].find((k) => k * mag >= raw) ?? 10;
  return f * mag * 1e9;
}

/**
 * Volume protegido e armazenado por dia, com a franquia como referência.
 * Um único eixo (GB). Rótulos diretos no fim de cada linha e legenda acima.
 */
export function UsageChart({ daily, quota }: { daily: UsageDay[]; quota: number }) {
  const [pointer, setPointer] = useState<{ i: number; boxWidth: number } | null>(null);
  const hover = pointer?.i ?? null;
  const t = useT(usageMessages).chart;
  const tc = useT(common);
  const { dayMonth, gb } = useFormat();

  const { x, y, yTicks, protPath, storedPath, areaPath, yMax } = useMemo(() => {
    const step = niceStep(Math.max(quota * 1.05, ...daily.map((d) => Math.max(d.protectedBytes, d.storedBytes))));
    const yMax = step * 4;
    const iw = W - M.left - M.right;
    const ih = H - M.top - M.bottom;
    const x = (i: number) => M.left + (daily.length <= 1 ? 0 : (i / (daily.length - 1)) * iw);
    const y = (v: number) => M.top + ih - (v / yMax) * ih;
    const yTicks = [0, 1, 2, 3, 4].map((k) => k * step);
    const line = (key: "protectedBytes" | "storedBytes") => daily.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join("");
    const protPath = line("protectedBytes");
    const areaPath = `${protPath}L${x(daily.length - 1)},${y(0)}L${x(0)},${y(0)}Z`;
    return { x, y, yTicks, protPath, storedPath: line("storedBytes"), areaPath, yMax };
  }, [daily, quota]);

  const last = daily[daily.length - 1];

  // Rótulos à direita (franquia e fim das linhas), afastados para não colidir.
  const labels = (() => {
    const raw = [
      ...(quota <= yMax ? [{ key: "quota", y: y(quota), text: t.quotaLabel(gb(quota)), className: "quota-label" }] : []),
      ...(last
        ? [
            { key: "prot", y: y(last.protectedBytes), text: gb(last.protectedBytes), className: "endlabel" },
            { key: "stored", y: y(last.storedBytes), text: gb(last.storedBytes), className: "endlabel" },
          ]
        : []),
    ].sort((a, b) => a.y - b.y);
    for (let i = 1; i < raw.length; i++) {
      const prev = raw[i - 1]!;
      const cur = raw[i]!;
      if (cur.y - prev.y < 15) cur.y = prev.y + 15;
    }
    return raw;
  })();
  const hovered = hover === null ? null : daily[hover];

  function onMove(e: React.PointerEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const iw = W - M.left - M.right;
    const i = Math.round(((px - M.left) / iw) * (daily.length - 1));
    const boxWidth = e.currentTarget.parentElement?.clientWidth ?? rect.width;
    setPointer({ i: Math.max(0, Math.min(daily.length - 1, i)), boxWidth });
  }

  const boxWidth = pointer?.boxWidth ?? W;
  const tipLeft = hover === null ? 0 : (x(hover) / W) * boxWidth;

  return (
    <div className="chart-box">
      <div className="legend" style={{ marginBottom: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span className="sw" style={{ background: "var(--series-1)", height: 3, width: 14 }} /> {t.legendProtected}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span className="sw" style={{ background: "var(--series-2)", height: 3, width: 14 }} /> {t.legendStored}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span className="sw" style={{ background: "var(--crit-mark)", height: 2, width: 14 }} /> {t.legendQuota}
        </span>
      </div>
      <svg
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t.summary(daily.length, last ? gb(last.storedBytes) : "—", gb(quota))}
        onPointerMove={onMove}
        onPointerLeave={() => setPointer(null)}
      >
        <g className="grid">
          {yTicks.map((t) => (
            <g key={t}>
              <line x1={M.left} x2={W - M.right} y1={y(t)} y2={y(t)} />
              <text x={M.left - 8} y={y(t) + 4} textAnchor="end">{gb(t)}</text>
            </g>
          ))}
        </g>
        <line className="baseline" x1={M.left} x2={W - M.right} y1={y(0)} y2={y(0)} />
        {daily.map((d, i) =>
          (i % 7 === 0 && i < daily.length - 4) || i === daily.length - 1 ? (
            <text key={d.date} x={x(i)} y={H - 8} textAnchor={i === 0 ? "start" : i === daily.length - 1 ? "end" : "middle"}>
              {i === daily.length - 1 ? tc.today : dayMonth(d.date)}
            </text>
          ) : null,
        )}
        {quota <= yMax && (
          <g>
            <line className="quota" x1={M.left} x2={W - M.right} y1={y(quota)} y2={y(quota)} />
          </g>
        )}
        <path d={areaPath} fill="var(--series-1)" fillOpacity={0.06} />
        <path d={storedPath} fill="none" stroke="var(--series-2)" strokeWidth={2} strokeLinejoin="round" />
        <path d={protPath} fill="none" stroke="var(--series-1)" strokeWidth={2} strokeLinejoin="round" />
        {last && (
          <>
            <circle cx={x(daily.length - 1)} cy={y(last.protectedBytes)} r={4} fill="var(--series-1)" stroke="var(--surface)" strokeWidth={2} />

            <circle cx={x(daily.length - 1)} cy={y(last.storedBytes)} r={4} fill="var(--series-2)" stroke="var(--surface)" strokeWidth={2} />
          </>
        )}
        {labels.map((l) => (
          <text key={l.key} className={l.className} x={W - M.right + 8} y={l.y + 4}>{l.text}</text>
        ))}
        {hovered && hover !== null && (
          <g>
            <line className="cross" x1={x(hover)} x2={x(hover)} y1={M.top} y2={y(0)} />
            <circle cx={x(hover)} cy={y(hovered.protectedBytes)} r={4} fill="var(--series-1)" stroke="var(--surface)" strokeWidth={2} />
            <circle cx={x(hover)} cy={y(hovered.storedBytes)} r={4} fill="var(--series-2)" stroke="var(--surface)" strokeWidth={2} />
          </g>
        )}
        <rect x={M.left} y={M.top} width={W - M.left - M.right} height={H - M.top - M.bottom} fill="transparent" />
      </svg>
      {hovered && (
        <div
          className="chart-tip"
          style={{ left: Math.min(Math.max(tipLeft - 90, 0), boxWidth - 190), top: 36 }}
          role="status"
        >
          <strong>{dayMonth(hovered.date)}</strong>
          <div className="row"><span className="sw" style={{ background: "var(--series-1)" }} />{t.tipProtected}<strong>{gb(hovered.protectedBytes)}</strong></div>
          <div className="row"><span className="sw" style={{ background: "var(--series-2)" }} />{t.tipStored}<strong>{gb(hovered.storedBytes)}</strong></div>
        </div>
      )}
    </div>
  );
}
